import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxButton, FmlxTooltip} from 'fmlx-common-ui';
import styled from 'styled-components';
import {convertObjectToStringCSS} from '../lib/objectHelper';

// this is a modified component of FmlxButton with the ability to alter button's inner properties
const FastButtonContainer = styled.div`
  ${(props) => props?.fitContainer && 'height: 100%;'}

  .fmlx-button {
    width: 100%;
    height: 100%;

    &__label {
      ${(props) => props?.withIcon && 'padding-top: 3px'}
    }
  }

  .fmlx-button__icon.md.primary-contain .MuiIconButton-root {
    ${(props) => props?.fitContainer && 'height: 100% !important;'}
  }

  .MuiButton-root {
    flex-wrap: wrap;
    align-content: center;
    ${(props) => props?.buttonStyle}
  }

  .MuiButton-sizeMd {
    ${(props) =>
      props?.staticSize &&
      `
    max-Width: 20rem;
    font-size: clamp(12px, 1.5vh, 1rem) !important;
    width: 10vw;
  `}
  }
`;

function FastButton({
  staticSize,
  fitContainer,
  disableLabelShortening,
  className,
  buttonStyle,
  label,
  height,
  withIcon,
  tooltipDescription,
  ...props
}) {
  const [cssStyling, setCssStyling] = useState('');
  const [labelState, setLabelState] = useState(label);
  const [isLabelShorten, setIsLabelShorten] = useState(false);

  // const
  const isWithIcon = withIcon !== 'disable';
  const minWidth = isWithIcon ? '174px' : '145px';
  const screenWidthBreakpoint = 1060;
  const characterBreakpoint = 10;
  // end const

  // #region label shortening
  const shortenLabel = useCallback(() => {
    if (typeof label !== 'string' || disableLabelShortening) return;

    const windowWidth = window.innerWidth;

    if (labelState.length > characterBreakpoint && windowWidth < screenWidthBreakpoint && !isLabelShorten) {
      setLabelState(`${labelState.substring(0, characterBreakpoint)}...`);
      setIsLabelShorten(true);
    } else if (windowWidth >= screenWidthBreakpoint && isLabelShorten) {
      setLabelState(label);
      setIsLabelShorten(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelState, label, disableLabelShortening, isLabelShorten]);

  // return label to normal if width is bigger than breakpoint and is currently shortened
  useEffect(() => {
    if (label !== labelState && !isLabelShorten) {
      setLabelState(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  useEffect(() => {
    window.addEventListener('resize', shortenLabel);
    return () => {
      window.removeEventListener('resize', shortenLabel);
    };
  }, [shortenLabel]);
  // #endRegion label shortening

  useEffect(() => {
    if (!buttonStyle) return;

    const cssStringbuttonStyle = convertObjectToStringCSS(buttonStyle, true);
    setCssStyling(cssStringbuttonStyle);
  }, [buttonStyle]);

  return (
    <FmlxTooltip title={tooltipDescription}>
      <div style={{height}}>
        <FastButtonContainer
          className={`fast-button ${className}`}
          staticSize={staticSize}
          fitContainer={fitContainer}
          buttonStyle={`${fitContainer && 'height: 100% !important; width: 100%;'}min-width: ${minWidth}!important;${cssStyling}`}
          withIcon={isWithIcon}
          minWidth={minWidth}
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FmlxButton label={labelState} withIcon={withIcon} {...props} />
        </FastButtonContainer>
      </div>
    </FmlxTooltip>
  );
}

FastButton.propTypes = {
  disableLabelShortening: PropTypes.bool,
  staticSize: PropTypes.bool,
  fitContainer: PropTypes.bool,
  withIcon: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  tooltipDescription: PropTypes.string,
  height: PropTypes.any,
  buttonStyle: PropTypes.object,
};

FastButton.defaultProps = {
  disableLabelShortening: false,
  staticSize: false,
  fitContainer: false,
  className: '',
  buttonStyle: null,
  label: '',
  height: '100%',
  withIcon: 'disable',
  tooltipDescription: '',
};

export default FastButton;
